<template>
  <a-drawer
    v-bind="getBindValue"
    :open="open"
    :keyboard="keyboard"
    :root-class-name="`${bem.b()} ${rootClassName}`"
    @close="handleClose"
  >
    <template v-if="$slots['closeIcon']" #closeIcon>
      <slot name="closeIcon" />
    </template>

    <!-- title插槽 -->
    <template #title>
      <slot name="title">{{ props.title }}</slot>
    </template>
    <!-- extra插槽 -->
    <template #extra>
      <slot name="extra" />
    </template>
    <!-- 内容区域 -->
    <div :class="[bem.e('container')]">
      <!-- 内容区域 -->
      <div ref="scrollRef" :class="[bem.e('content')]">
        <slot />
      </div>
    </div>
    <!-- 底部区域 -->
    <template v-if="showFooter" #footer>
      <div
        :class="`${bem.e('footer')} ${
          isShowContentBottomBox ? bem.e('footer-shadow') : ''
        }`"
      >
        <slot name="footer">
          <div :class="bem.e('footer-left')">
            <slot name="footer-left" />
          </div>
          <div :class="bem.e('footer-right')">
            <slot name="footer-right">
              <ll-button
                :class="bem.m('cancel')"
                :label="cancelText"
                v-bind="cancelButtonProps"
                @click="handleCancel"
              >
              </ll-button>
              <ll-button
                type="primary"
                :loading="confirmLoading"
                :label="confirmText"
                v-bind="okButtonProps"
                @click="handleConfirm"
              >
              </ll-button>
            </slot>
          </div>
        </slot>
      </div>
    </template>
  </a-drawer>
</template>

<script setup lang="ts">
import { computed, useAttrs } from 'vue'

// import { Drawer } from 'ant-design-vue'

import { useScroll } from '@ll-plus/hooks'
import { createNamespace } from '@ll-plus/utils'
import { omit } from 'lodash-es'
import { newDrawerEmits, newDrawerProps } from './drawer'

import type { DrawerProps } from 'ant-design-vue'

defineOptions({ name: 'LlNewDrawer' })

// 1.props/emits
const props = defineProps(newDrawerProps)
const emits = defineEmits(newDrawerEmits)

// 2.hooks
const attrs = useAttrs()
const { scrollPosition, isHasScrollBar, scrollRef } = useScroll()

// 3.ref/computed/const
const getBindValue = computed(() => {
  const result: DrawerProps = {
    ...omit({ ...attrs }, [...Object.keys(newDrawerProps), 'rootClassName']),
  }
  if (!props.showFooter) result.footer = null
  return result
})
// 是否显示底部的阴影div
const isShowContentBottomBox = computed(() => {
  // 如果顶部的阴影为true则说明是内部的容器在滚动产品的阴影 则由容器的滚动情况来决定阴影
  if (props.footerShadowPlacement === 'inner') {
    return (
      props.isShowFooterShadow &&
      isHasScrollBar.value &&
      scrollPosition.value !== 'bottom'
    )
  } else {
    // 如果顶部的阴影为false，则说明不是内部容器滚动，则底部的阴影由 外部的isShowFooterShadow来决定
    return props.isShowFooterShadow
  }
})
const bem = createNamespace('new-drawer')

// 4.methods
// 点击关闭icon
const handleClose = (): void => {
  emits('close')
  emits('update:open')
}
// 点击取消按钮
const handleCancel = (): void => {
  emits('cancel')
  emits('update:open')
}
// 点击确定按钮
const handleConfirm = (): void => {
  emits('confirm')
}
// 滚动条返回顶部
const scrollTop = () => {
  if (scrollRef && scrollRef.value) {
    scrollRef.value.scrollTop = 0
    scrollPosition.value = 'top'
  }
}

// 4.defineExpose
defineExpose({
  scrollTop,
})
</script>
